// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The selected ramp's control points, in Three (M13-24).
//
// A small sphere at every control point of the selected ramp, up in the air where the point is,
// room-sized through the bench's scale so a fingertip covers one at any scale: the `selected`
// finish, the one under a hand brighter, the one in hand brighter still. While a point is in
// hand the curve is drawn too — a thin tube along the simulation's own `Path` through the
// points as the draft has them now, in the `ghost` finish — because the machine's own ramp
// waits for the release (the host adopts once) and an author shaping a ramp has to see the
// ramp they are shaping. It is the carry's ghost, for a curve.
//
// Everything is under `playfield`, in the playfield's frame (PH-001), where the points are.

import { BufferAttribute, BufferGeometry, Group, Mesh, MeshBasicMaterial, SphereGeometry } from 'three'
import { Path } from '@core/sim/Path'
import { PROFILE_SAMPLES_PER_SEGMENT } from '../builder/splines'
import { HANDLE_RADIUS, type PointsView } from '../xr/build/rampPoints'
import { FINISHES } from './partsKit'
import { tubeTriangles, type Vec3 } from './tubePath'

export const HANDLE_COLOR = FINISHES.selected.color
export const HANDLE_OPACITY = 0.55
export const HANDLE_HOVER_OPACITY = 0.85
export const HANDLE_HELD_OPACITY = 1
/** A point carried past the playfield's edge: faded, because a release there takes it off. */
export const HANDLE_LEAVING_OPACITY = 0.2
export const CURVE_COLOR = FINISHES.ghost.color
export const CURVE_OPACITY = FINISHES.ghost.opacity ?? 0.45
/** The shaped curve's radius, room metres: a wire, not a ramp — it shows the path, the machine shows the ramp. */
export const CURVE_RADIUS = 0.003

export interface RampHandlesBuild {
  readonly group: Group
  update(view: PointsView | undefined, scale: number): void
  /** What the handles were last built for, for a test with no renderer to read. */
  readonly key: string | undefined
  /** Whether the shaped curve is drawn. */
  readonly curveDrawn: boolean
  dispose(): void
}

export function buildRampHandles(): RampHandlesBuild {
  const group = new Group()
  group.name = 'build.points'
  group.visible = false
  const sphere = new SphereGeometry(HANDLE_RADIUS, 12, 8)
  let handles: { mesh: Mesh; material: MeshBasicMaterial }[] = []
  let curve: { mesh: Mesh; geometry: BufferGeometry; material: MeshBasicMaterial } | undefined
  let keyNow: string | undefined

  const dropHandles = (): void => {
    for (const h of handles) {
      group.remove(h.mesh)
      h.material.dispose()
    }
    handles = []
  }
  const dropCurve = (): void => {
    if (curve === undefined) return
    group.remove(curve.mesh)
    curve.geometry.dispose()
    curve.material.dispose()
    curve = undefined
  }

  const showCurve = (view: PointsView, inverse: number): void => {
    dropCurve()
    if (view.spline.length < 2) return
    const path = new Path(view.spline as readonly (readonly [number, number, number])[], PROFILE_SAMPLES_PER_SEGMENT)
    const points: Vec3[] = []
    for (let i = 0; i < path.xs.length; i++) {
      const at: Vec3 = [path.xs[i] as number, path.ys[i] as number, path.zs[i] as number]
      const last = points[points.length - 1]
      if (last !== undefined && Math.hypot(at[0] - last[0], at[1] - last[1], at[2] - last[2]) < 1e-9) continue
      points.push(at)
    }
    if (points.length < 2) return
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(tubeTriangles(points, CURVE_RADIUS * inverse), 3))
    geometry.computeVertexNormals()
    const material = new MeshBasicMaterial({ color: CURVE_COLOR, transparent: true, opacity: CURVE_OPACITY, depthWrite: false })
    const mesh = new Mesh(geometry, material)
    mesh.name = 'points-curve'
    group.add(mesh)
    curve = { mesh, geometry, material }
  }

  return {
    group,
    update(view, scale) {
      if (view === undefined) {
        dropHandles()
        dropCurve()
        keyNow = undefined
        group.visible = false
        return
      }
      group.visible = true
      const inverse = scale > 0 ? 1 / scale : 1
      if (view.key !== keyNow) {
        keyNow = view.key
        // As many spheres as points; each lit for what it is this frame.
        while (handles.length > view.points.length) {
          const h = handles.pop()
          if (h === undefined) break
          group.remove(h.mesh)
          h.material.dispose()
        }
        while (handles.length < view.points.length) {
          const material = new MeshBasicMaterial({ color: HANDLE_COLOR, transparent: true, opacity: HANDLE_OPACITY, depthWrite: false })
          const mesh = new Mesh(sphere, material)
          group.add(mesh)
          handles.push({ mesh, material })
        }
        view.points.forEach((point, i) => {
          const h = handles[i]
          if (h === undefined) return
          h.mesh.name = `point-${view.layerId}-${point.index}`
          h.mesh.position.set(point.at[0], point.at[1], point.at[2])
          h.material.opacity = point.leaving
            ? HANDLE_LEAVING_OPACITY
            : point.held
              ? HANDLE_HELD_OPACITY
              : point.hover !== undefined
                ? HANDLE_HOVER_OPACITY
                : HANDLE_OPACITY
        })
        if (view.shaping) showCurve(view, inverse)
        else dropCurve()
      }
      // Room-sized whatever the bench's scale: a handle a fingertip covers.
      for (const h of handles) h.mesh.scale.set(inverse, inverse, inverse)
    },
    get key() {
      return keyNow
    },
    get curveDrawn() {
      return curve !== undefined
    },
    dispose() {
      dropHandles()
      dropCurve()
      sphere.dispose()
    },
  }
}
